import { Flex, Spoiler, Text } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import { type CoreAddress } from '@flecto/types'
import { Badge, TextDate } from '@flecto/ui'

type TextBusinessHoursProps = {
  address?: CoreAddress
}

export function TextBusinessHours({ address }: TextBusinessHoursProps) {
  const { t } = useTranslation('store', { keyPrefix: 'store-shared' })

  if (!address) {
    return
  }

  const weekdays = {
    monday: {
      label: t('text_business_hours.label_day_monday'),
      position: 1,
    },
    tuesday: {
      label: t('text_business_hours.label_day_tuesday'),
      position: 2,
    },
    wednesday: {
      label: t('text_business_hours.label_day_wednesday'),
      position: 3,
    },
    thursday: {
      label: t('text_business_hours.label_day_thursday'),
      position: 4,
    },
    friday: {
      label: t('text_business_hours.label_day_friday'),
      position: 5,
    },
    saturday: {
      label: t('text_business_hours.label_day_saturday'),
      position: 6,
    },
    sunday: {
      label: t('text_business_hours.label_day_sunday'),
      position: 0,
    },
  }

  const getHoursCustom = (day: number) => {
    const schedule = (address.operating_hours_schedule ?? {})[day] ?? []

    if (schedule.length <= 0) {
      return (
        <Badge color='gray' size='sm' mt={1} style={{ flexShrink: 0 }}>
          {t('text_business_hours.label_closed')}
        </Badge>
      )
    }

    const from = schedule[0][0]
    const to = schedule[0][1]

    return (
      <Badge color='blue' size='sm' mt={1} style={{ flexShrink: 0 }}>
        <Flex align='center' gap='xs'>
          <TextDate size='xs' date={from} variant='time-brief' /> -
          <TextDate size='xs' date={to} variant='time-brief' />
        </Flex>
      </Badge>
    )
  }

  switch (address.operating_hours_type) {
    case 'always_open':
      return (
        <Flex align='center' justify='space-between' gap='xs'>
          <Text>{t('text_business_hours.label_everyday')}</Text>
          <Badge size='sm' color='blue' mt={1} style={{ flexShrink: 0 }}>
            {t('text_business_hours.label_open_24_hours')}
          </Badge>
        </Flex>
      )

    default:
    case 'everyday':
      return (
        <Flex align='center' justify='space-between' gap='xs'>
          <Text>{t('text_business_hours.label_everyday')}</Text>
          <Badge size='sm' color='blue' mt={1} style={{ flexShrink: 0 }}>
            09:00 - 18:00
          </Badge>
        </Flex>
      )

    case 'weekdays':
      return (
        <Flex align='center' justify='space-between' gap='xs'>
          <Text>{t('text_business_hours.label_weekdays_only')}</Text>
          <Badge size='sm' color='blue' mt={1} style={{ flexShrink: 0 }}>
            09:00 - 18:00
          </Badge>
        </Flex>
      )

    case 'custom':
      return (
        <Spoiler
          maxHeight={20}
          showLabel={
            <Text size='sm'>{t('text_business_hours.label_show_all')}</Text>
          }
          hideLabel={
            <Text size='sm'>{t('text_business_hours.label_close')}</Text>
          }
        >
          {Object.keys(weekdays).map((day) => (
            <Flex key={day} align='center' justify='space-between' mb='xs'>
              <Text>{weekdays[day as keyof typeof weekdays].label}</Text>
              {getHoursCustom(weekdays[day as keyof typeof weekdays].position)}
            </Flex>
          ))}
        </Spoiler>
      )
  }
}
